import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import LoadingComponent from "../../common/LoadingComponent";
import PageRenderer from "../components/PageRenderer";
import { useChunkCache } from "../cache/useChunkCache";
import "../styles/report-preview.css";
import "../../PreviewPdf/PreviewPDF.css";

const PRINT_BATCH_SIZE = 15;

const ReportPrintView = ({
  propertyMasterId,
  inspectionReferenceNumber,
  clientNameParam,
}) => {
  const [searchParams] = useSearchParams();
  const [renderedCount, setRenderedCount] = useState(0);
  const [preparing, setPreparing] = useState(true);

  const {
    chunkCache,
    pageModel,
    loading,
    error,
    loadAllChunks,
    allChunksLoaded,
  } = useChunkCache({
    propertyMasterId,
    inspectionReferenceNumber,
    enabled: true,
  });

  const routeParams = {
    clientNameParam,
    propertyMasterId,
    inspectionReferenceNumber,
  };

  useEffect(() => {
    if (loading) return;
    let cancelled = false;

    (async () => {
      setPreparing(true);
      await loadAllChunks();
      if (!cancelled) setPreparing(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [loading, loadAllChunks]);

  useEffect(() => {
    if (!allChunksLoaded || !pageModel.length) return;

    if (renderedCount >= pageModel.length) {
      if (searchParams.get("autoPrint") === "1") {
        window.setTimeout(() => window.print(), 500);
      }
      return;
    }

    const id = requestAnimationFrame(() => {
      setRenderedCount((c) =>
        Math.min(c + PRINT_BATCH_SIZE, pageModel.length),
      );
    });

    return () => cancelAnimationFrame(id);
  }, [allChunksLoaded, pageModel.length, renderedCount, searchParams]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const pagesToRender = pageModel.slice(0, renderedCount);
  const isRendering =
    preparing || loading || (allChunksLoaded && renderedCount < pageModel.length);

  return (
    <div className="report-print">
      <header className="report-preview__toolbar no-print">
        <Link
          to={`/client/${clientNameParam}/report/preview/${propertyMasterId}/${inspectionReferenceNumber}`}
          className="report-preview__back"
        >
          Back to preview
        </Link>
        <h1 className="report-preview__title">Print Report</h1>
        <button
          type="button"
          className="report-preview__print-btn"
          onClick={handlePrint}
          disabled={isRendering || !pageModel.length}
        >
          Print
        </button>
      </header>

      {error && (
        <p className="report-preview__error no-print">
          Failed to load report for printing.
        </p>
      )}

      {isRendering && (
        <p className="report-preview__progress no-print">
          Preparing report for print… ({renderedCount}/{pageModel.length || "…"}
          pages)
        </p>
      )}

      <div className="report-print__document preview-pdf-wrapper">
        <div className="report-preview__pages report-preview__pages--print">
          {pagesToRender.map((page) => (
            <div key={page.id} className="report-print__page-wrap">
              <PageRenderer
                page={page}
                chunkCache={chunkCache}
                routeParams={routeParams}
              />
            </div>
          ))}
        </div>
        <LoadingComponent loading={loading || preparing} />
      </div>
    </div>
  );
};

export default ReportPrintView;
